import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { type SalesLeadStatus } from './salesLead'

export enum PipelinePageStage {
  WON = 'WON',
  NEGOTIATION = 'NEGOTIATION',
  LOST = 'LOST',
  WARM = 'WARM',
  HIDDEN = 'HIDDEN',
}

export enum DealValueSource {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
}

/** Where the rep put a lead on the page; AUTO lets its status decide. */
export enum PipelineSection {
  AUTO = 'AUTO',
  DEAL = 'DEAL',
  WARM = 'WARM',
  HIDDEN = 'HIDDEN',
}

export enum LeadTemperature {
  HOT = 'HOT',
  WARM = 'WARM',
  TEPID = 'TEPID',
  COLD = 'COLD',
}

export const temperatureLabels: Record<LeadTemperature, string> = {
  [LeadTemperature.HOT]: 'Hot',
  [LeadTemperature.WARM]: 'Warm',
  [LeadTemperature.TEPID]: 'Tepid',
  [LeadTemperature.COLD]: 'Cold',
}

export const temperatureColors: Record<LeadTemperature, string> = {
  [LeadTemperature.HOT]: 'bg-red-100 text-red-700',
  [LeadTemperature.WARM]: 'bg-amber-100 text-amber-700',
  [LeadTemperature.TEPID]: 'bg-muted text-muted-foreground',
  [LeadTemperature.COLD]: 'bg-slate-100 text-slate-700',
}

export const pipelineStageLabels: Record<PipelinePageStage, string> = {
  [PipelinePageStage.WON]: 'Won',
  [PipelinePageStage.NEGOTIATION]: 'Open',
  [PipelinePageStage.LOST]: 'Lost',
  [PipelinePageStage.WARM]: 'Warm',
  [PipelinePageStage.HIDDEN]: 'Hidden',
}

export interface PipelineContact {
  name: string
  phone: string
  email: string
}

export interface PipelineNote {
  text: string
  createdAt: string
}

export interface PipelineDeal {
  id: string
  leadId: string
  legalName: string
  dbaName: string
  dotNumber: string | null
  primaryContact: PipelineContact | null
  dealValue: string | null
  dealValueSource: DealValueSource
  weight: string
  pageStage: PipelinePageStage
  status: SalesLeadStatus
  nextAction: string
  nextActionDate: string | null
  nextActionOverdue: boolean
  latestNote: PipelineNote | null
  expectedCloseMonth: string | null
  carriedFrom: string | null
  isEndorsement: boolean
  parentLeadId: string | null
  pipelineSection: PipelineSection
  /** A bound or in-force policy keeps it Won, so it can't be moved or removed. */
  heldByPolicy: boolean
}

export interface WarmAccount {
  id: string
  legalName: string
  dbaName: string
  dotNumber: string | null
  state: string
  powerUnits: number
  primaryContact: PipelineContact | null
  status: SalesLeadStatus
  /** Empty until a rep or the sheet import sets one. */
  temperature: LeadTemperature | ''
  conversionScore: number | null
  lastActivityAt: string | null
  daysSinceTouch: number | null
  isUnresponsive: boolean
  sequenceRunning: boolean
  nextStep: string
  nextStepDate: string | null
  pipelineSection: PipelineSection
}

export interface PipelineStats {
  totalPipeline: string
  expectedRevenue: string
  wonValue: string
  valuedDeals: number
  valuedOpenDeals: number
  openDeals: number
  won: number
  lost: number
  winRate: number | null
  dealsWithoutValue: number
  nextActionsOverdue: number
}

export interface WarmStats {
  total: number
  hot: number
  followUpsDue: number
}

export interface MyPipeline {
  month: string
  rep: { id: string; name: string; avatar: string }
  /** Due dates are the rep's own reminders, editable only when the rep is the one viewing. */
  viewerIsRep: boolean
  stats: PipelineStats
  deals: PipelineDeal[]
  warmAccounts: WarmAccount[]
  warmStats: WarmStats
}

/** Fields of a lead the My Pipeline page can edit inline. */
export interface PipelineLeadFields {
  dealValue: string | null
  dealValueSource: DealValueSource
  nextAction: string
  expectedCloseMonth: string | null
  temperature: LeadTemperature | ''
  pipelineSection: PipelineSection
}

function getMyPipeline(month: string, repId?: string): Promise<MyPipeline> {
  const params: Record<string, string> = { month }
  if (repId) params.rep = repId
  return axios
    .get('/api/v1/insure/internal/sales-leads/my-pipeline/', { params })
    .then((response) => toCamelCase(response.data) as MyPipeline)
}

export function useMyPipeline(month: string, repId?: string) {
  return useQuery({
    queryKey: [CacheKeys.MY_PIPELINE, month, repId ?? 'me'],
    queryFn: () => getMyPipeline(month, repId),
  })
}

export function useUpdateLeadPipelineFields() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<PipelineLeadFields>) =>
      axios.patch(`/api/v1/insure/internal/sales-leads/${id}/`, toSnakeCase(patch)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_PIPELINE] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}
